import { useMemo } from "react";
import { ApolloError, useQuery } from "@apollo/client";
import { LOAD_AGENCY_RESOURCES } from "../../../apis/queries/loadAgencyResources";
import { activeLocale } from "../../../resources/strings";
import {
  AgencyResourceItem,
  ResourceViewModel,
  toResourceViewModels,
} from "../resourceUtils";

const VARIABLES = { input: { active: true } } as const;

type LoadAgencyResourcesData = {
  loadAgencyResources: Array<AgencyResourceItem | null> | null;
};

export type AgencyResourcesState = {
  resources: ResourceViewModel[];
  /** True once a response has arrived, even an empty one. */
  loaded: boolean;
  loading: boolean;
  error: ApolloError | undefined;
};

/**
 * The participant's agency resources, ready to render. The agency comes from
 * the participant's own case on the server, so nothing here can widen access.
 */
export const useAgencyResources = (
  language: string = activeLocale,
): AgencyResourcesState => {
  const { data, loading, error } = useQuery<LoadAgencyResourcesData>(
    LOAD_AGENCY_RESOURCES,
    {
      variables: VARIABLES,
      fetchPolicy: "cache-and-network",
    },
  );
  const resources = useMemo(
    () => toResourceViewModels(data?.loadAgencyResources, language),
    [data, language],
  );
  return { resources, loaded: data !== undefined, loading, error };
};
